// ============================================================
// Wauvea WebXR — VR Overlay Controls (v4)
// 3D in-scene controls for immersive XR.
// Solid black, rounded corners, close/reopen, ray blocking.
// ============================================================

import * as THREE from 'three';

export class VROverlay {
  constructor(scene, renderer, mainScreen) {
    this.scene = scene;
    this.renderer = renderer;
    this.mainScreen = mainScreen;
    this.group = new THREE.Group();
    this.group.visible = false;
    this.group.name = 'vr-overlay';

    this._clickables = [];
    this._allMeshes = [];
    this._isVisible = false;
    this._isOpen = true;  // Whether overlay should show (vs user closed it)

    // Text canvas
    this._textCanvas = document.createElement('canvas');
    this._textCanvas.width = 512;
    this._textCanvas.height = 64;
    this._textCtx = this._textCanvas.getContext('2d');
    this._textTexture = new THREE.CanvasTexture(this._textCanvas);
    this._textTexture.minFilter = THREE.LinearFilter;

    // Progress canvas
    this._progCanvas = document.createElement('canvas');
    this._progCanvas.width = 512;
    this._progCanvas.height = 16;
    this._progCtx = this._progCanvas.getContext('2d');
    this._progTexture = new THREE.CanvasTexture(this._progCanvas);
    this._progTexture.minFilter = THREE.LinearFilter;

    this._lastTitle = '';
    this._lastTime = '';
    this._lastFraction = -1;

    this._build();
    scene.add(this.group);
  }

  // ─── Rounded rectangle shape ──────────────────────────────────
  _makeRoundedRectShape(w, h, r) {
    const shape = new THREE.Shape();
    const hw = w / 2, hh = h / 2;
    shape.moveTo(-hw + r, -hh);
    shape.lineTo(hw - r, -hh);
    shape.quadraticCurveTo(hw, -hh, hw, -hh + r);
    shape.lineTo(hw, hh - r);
    shape.quadraticCurveTo(hw, hh, hw - r, hh);
    shape.lineTo(-hw + r, hh);
    shape.quadraticCurveTo(-hw, hh, -hw, hh - r);
    shape.lineTo(-hw, -hh + r);
    shape.quadraticCurveTo(-hw, -hh, -hw + r, -hh);
    return shape;
  }

  _build() {
    // ── Solid black rounded panel ──
    const panelShape = this._makeRoundedRectShape(2.0, 0.5, 0.06);
    const panelGeo = new THREE.ShapeGeometry(panelShape);
    const panelMat = new THREE.MeshBasicMaterial({
      color: 0x000000,
      side: THREE.DoubleSide,
      depthWrite: true,
      depthTest: true,
    });
    this._panel = new THREE.Mesh(panelGeo, panelMat);
    this._panel.renderOrder = 9999;
    this._panel.userData.action = 'panel';
    this.group.add(this._panel);
    this._allMeshes.push(this._panel);

    // ── Play/Pause button ──
    const btnGeo = new THREE.CircleGeometry(0.1, 32);
    const btnMat = new THREE.MeshBasicMaterial({ color: 0x6C5CE7 });
    this._btnPlay = new THREE.Mesh(btnGeo, btnMat);
    this._btnPlay.position.set(0, 0.04, 0.002);
    this._btnPlay.renderOrder = 10000;
    this._btnPlay.userData.action = 'togglePlay';
    this.group.add(this._btnPlay);
    this._clickables.push(this._btnPlay);
    this._allMeshes.push(this._btnPlay);

    // Play icon
    const playShape = new THREE.Shape();
    playShape.moveTo(-0.03, -0.045);
    playShape.lineTo(-0.03, 0.045);
    playShape.lineTo(0.05, 0);
    playShape.closePath();
    this._playIcon = new THREE.Mesh(
      new THREE.ShapeGeometry(playShape),
      new THREE.MeshBasicMaterial({ color: 0xffffff })
    );
    this._playIcon.position.set(0.008, 0.04, 0.003);
    this._playIcon.renderOrder = 10001;
    this.group.add(this._playIcon);

    // Pause icon
    const barGeo = new THREE.PlaneGeometry(0.02, 0.065);
    this._pauseIcon = new THREE.Group();
    const bar1 = new THREE.Mesh(barGeo, new THREE.MeshBasicMaterial({ color: 0xffffff }));
    bar1.position.x = -0.02;
    const bar2 = new THREE.Mesh(barGeo, new THREE.MeshBasicMaterial({ color: 0xffffff }));
    bar2.position.x = 0.02;
    this._pauseIcon.add(bar1, bar2);
    this._pauseIcon.position.set(0, 0.04, 0.003);
    this._pauseIcon.renderOrder = 10001;
    this._pauseIcon.visible = false;
    this.group.add(this._pauseIcon);

    // ── Rewind ──
    this._btnRwd = this._makeRoundBtn(-0.3, 0.04);
    this._btnRwd.userData.action = 'rewind';
    this._clickables.push(this._btnRwd);
    this._allMeshes.push(this._btnRwd);
    this._makeArrowIcon(-0.3, 0.04, -1);

    // ── Forward ──
    this._btnFwd = this._makeRoundBtn(0.3, 0.04);
    this._btnFwd.userData.action = 'forward';
    this._clickables.push(this._btnFwd);
    this._allMeshes.push(this._btnFwd);
    this._makeArrowIcon(0.3, 0.04, 1);

    // ── Close (X) button — top right ──
    const closeGeo = new THREE.CircleGeometry(0.04, 16);
    const closeMat = new THREE.MeshBasicMaterial({ color: 0x444455 });
    this._btnClose = new THREE.Mesh(closeGeo, closeMat);
    this._btnClose.position.set(0.92, 0.18, 0.003);
    this._btnClose.renderOrder = 10002;
    this._btnClose.userData.action = 'close';
    this.group.add(this._btnClose);
    this._clickables.push(this._btnClose);
    this._allMeshes.push(this._btnClose);

    // X icon
    const xGroup = new THREE.Group();
    const xBar1 = new THREE.Mesh(
      new THREE.PlaneGeometry(0.04, 0.008),
      new THREE.MeshBasicMaterial({ color: 0xcccccc })
    );
    xBar1.rotation.z = Math.PI / 4;
    const xBar2 = new THREE.Mesh(
      new THREE.PlaneGeometry(0.04, 0.008),
      new THREE.MeshBasicMaterial({ color: 0xcccccc })
    );
    xBar2.rotation.z = -Math.PI / 4;
    xGroup.add(xBar1, xBar2);
    xGroup.position.set(0.92, 0.18, 0.004);
    xGroup.renderOrder = 10003;
    this.group.add(xGroup);

    // ── Text display ──
    const textGeo = new THREE.PlaneGeometry(1.6, 0.12);
    const textMat = new THREE.MeshBasicMaterial({ map: this._textTexture, transparent: true });
    this._textMesh = new THREE.Mesh(textGeo, textMat);
    this._textMesh.position.set(0, 0.18, 0.002);
    this._textMesh.renderOrder = 10001;
    this.group.add(this._textMesh);

    // ── Progress bar ──
    const progGeo = new THREE.PlaneGeometry(1.6, 0.035);
    const progMat = new THREE.MeshBasicMaterial({ map: this._progTexture, transparent: true });
    this._progMesh = new THREE.Mesh(progGeo, progMat);
    this._progMesh.position.set(0, -0.1, 0.002);
    this._progMesh.renderOrder = 10000;
    this._progMesh.userData.action = 'seek';
    this.group.add(this._progMesh);
    this._clickables.push(this._progMesh);
    this._allMeshes.push(this._progMesh);

    // ── Canvas icon helper ──
    this._makeIconTexture = (drawFn, size = 64) => {
      const c = document.createElement('canvas');
      c.width = size; c.height = size;
      const ctx = c.getContext('2d');
      drawFn(ctx, size);
      const tex = new THREE.CanvasTexture(c);
      tex.colorSpace = THREE.SRGBColorSpace;
      return tex;
    };

    // ── Volume/Mute button — bottom right ──
    const volUnmuteTex = this._makeIconTexture((ctx, s) => {
      ctx.fillStyle = '#cccccc';
      ctx.fillRect(s*0.15, s*0.3, s*0.18, s*0.4);
      ctx.beginPath();
      ctx.moveTo(s*0.33, s*0.2); ctx.lineTo(s*0.55, s*0.08);
      ctx.lineTo(s*0.55, s*0.92); ctx.lineTo(s*0.33, s*0.8);
      ctx.fill();
      ctx.strokeStyle = '#cccccc'; ctx.lineWidth = 3; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.arc(s*0.55, s*0.5, s*0.15, -0.8, 0.8); ctx.stroke();
      ctx.beginPath(); ctx.arc(s*0.55, s*0.5, s*0.28, -0.7, 0.7); ctx.stroke();
    });
    const volMuteTex = this._makeIconTexture((ctx, s) => {
      ctx.fillStyle = '#888888';
      ctx.fillRect(s*0.1, s*0.3, s*0.18, s*0.4);
      ctx.beginPath();
      ctx.moveTo(s*0.28, s*0.2); ctx.lineTo(s*0.48, s*0.08);
      ctx.lineTo(s*0.48, s*0.92); ctx.lineTo(s*0.28, s*0.8);
      ctx.fill();
      ctx.strokeStyle = '#ff4444'; ctx.lineWidth = 5; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(s*0.58, s*0.28); ctx.lineTo(s*0.88, s*0.72); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(s*0.88, s*0.28); ctx.lineTo(s*0.58, s*0.72); ctx.stroke();
    });
    this._volUnmuteTex = volUnmuteTex;
    this._volMuteTex = volMuteTex;

    const volBtnGeo = new THREE.CircleGeometry(0.03, 24);
    const volBtnMat = new THREE.MeshBasicMaterial({ color: 0x444455 });
    this._btnVol = new THREE.Mesh(volBtnGeo, volBtnMat);
    this._btnVol.position.set(0.46, 0.18, 0.003);
    this._btnVol.renderOrder = 10002;
    this._btnVol.userData.action = 'toggleMute';
    this.group.add(this._btnVol);
    this._clickables.push(this._btnVol);
    this._allMeshes.push(this._btnVol);

    const volIconGeo = new THREE.PlaneGeometry(0.04, 0.04);
    this._volIconMat = new THREE.MeshBasicMaterial({ map: volUnmuteTex, transparent: true });
    this._volIconMesh = new THREE.Mesh(volIconGeo, this._volIconMat);
    this._volIconMesh.position.set(0.46, 0.18, 0.004);
    this._volIconMesh.renderOrder = 10003;
    this.group.add(this._volIconMesh);

    // Volume slider track (compact)
    const volTrackGeo = new THREE.PlaneGeometry(0.3, 0.015);
    const volTrackMat = new THREE.MeshBasicMaterial({ color: 0x333344 });
    this._volTrack = new THREE.Mesh(volTrackGeo, volTrackMat);
    this._volTrack.position.set(0.68, 0.18, 0.003);
    this._volTrack.renderOrder = 10002;
    this._volTrack.userData.action = 'volumeSlider';
    this.group.add(this._volTrack);
    this._clickables.push(this._volTrack);
    this._allMeshes.push(this._volTrack);

    const volFillGeo = new THREE.PlaneGeometry(0.3, 0.015);
    const volFillMat = new THREE.MeshBasicMaterial({ color: 0x6C5CE7 });
    this._volFill = new THREE.Mesh(volFillGeo, volFillMat);
    this._volFill.position.set(0.68, 0.18, 0.004);
    this._volFill.renderOrder = 10003;
    this.group.add(this._volFill);
    this._volume = 1.0;

    const volKnobGeo = new THREE.CircleGeometry(0.014, 16);
    const volKnobMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    this._volKnob = new THREE.Mesh(volKnobGeo, volKnobMat);
    this._volKnob.position.set(0.83, 0.18, 0.005);
    this._volKnob.renderOrder = 10004;
    this.group.add(this._volKnob);

    // ── Follow/Pin toggle — top left ──
    this._isFollowing = false;
    const pinBtnGeo = new THREE.CircleGeometry(0.04, 16);
    const pinBtnMat = new THREE.MeshBasicMaterial({ color: 0x444455 });
    this._btnPin = new THREE.Mesh(pinBtnGeo, pinBtnMat);
    this._btnPin.position.set(-0.92, 0.18, 0.003);
    this._btnPin.renderOrder = 10002;
    this._btnPin.userData.action = 'toggleFollow';
    this.group.add(this._btnPin);
    this._clickables.push(this._btnPin);
    this._allMeshes.push(this._btnPin);

    const pinTex = this._makeIconTexture((ctx, s) => {
      ctx.strokeStyle = '#cccccc'; ctx.lineWidth = 4; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.arc(s*0.5, s*0.35, s*0.22, 0, Math.PI*2); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(s*0.5, s*0.57); ctx.lineTo(s*0.5, s*0.9); ctx.stroke();
    });
    const followTex = this._makeIconTexture((ctx, s) => {
      ctx.strokeStyle = '#88ff88'; ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(s*0.1, s*0.5);
      ctx.quadraticCurveTo(s*0.5, s*0.1, s*0.9, s*0.5);
      ctx.quadraticCurveTo(s*0.5, s*0.9, s*0.1, s*0.5);
      ctx.stroke();
      ctx.fillStyle = '#88ff88';
      ctx.beginPath(); ctx.arc(s*0.5, s*0.5, s*0.12, 0, Math.PI*2); ctx.fill();
    });
    this._pinTex = pinTex;
    this._followTex = followTex;

    const pinIconGeo = new THREE.PlaneGeometry(0.05, 0.05);
    this._pinIconMat = new THREE.MeshBasicMaterial({ map: pinTex, transparent: true });
    this._pinIconMesh = new THREE.Mesh(pinIconGeo, this._pinIconMat);
    this._pinIconMesh.position.set(-0.92, 0.18, 0.004);
    this._pinIconMesh.renderOrder = 10003;
    this.group.add(this._pinIconMesh);

    // ── Subtle purple border ──
    const borderShape = this._makeRoundedRectShape(2.04, 0.54, 0.07);
    const borderGeo = new THREE.ShapeGeometry(borderShape);
    const borderMat = new THREE.MeshBasicMaterial({
      color: 0x6C5CE7, transparent: true, opacity: 0.2,
    });
    const border = new THREE.Mesh(borderGeo, borderMat);
    border.position.z = -0.001;
    border.renderOrder = 9998;
    this.group.add(border);

    this.group.position.set(0, 4.2, -5.0);
  }

  _makeRoundBtn(x, y) {
    const geo = new THREE.CircleGeometry(0.065, 24);
    const mat = new THREE.MeshBasicMaterial({ color: 0x2a2a3c });
    const btn = new THREE.Mesh(geo, mat);
    btn.position.set(x, y, 0.002);
    btn.renderOrder = 10000;
    this.group.add(btn);
    return btn;
  }

  _makeArrowIcon(x, y, dir) {
    const shape = new THREE.Shape();
    shape.moveTo(-0.02 * dir, -0.025);
    shape.lineTo(-0.02 * dir, 0.025);
    shape.lineTo(0.025 * dir, 0);
    shape.closePath();
    const mesh = new THREE.Mesh(
      new THREE.ShapeGeometry(shape),
      new THREE.MeshBasicMaterial({ color: 0xddddee })
    );
    mesh.position.set(x, y, 0.003);
    mesh.renderOrder = 10001;
    this.group.add(mesh);
  }

  // ─── Canvas Text ──────────────────────────────────────────────
  _renderText(title, timeStr) {
    if (title === this._lastTitle && timeStr === this._lastTime) return;
    this._lastTitle = title;
    this._lastTime = timeStr;

    const ctx = this._textCtx;
    const w = this._textCanvas.width;
    const h = this._textCanvas.height;
    ctx.clearRect(0, 0, w, h);

    ctx.font = '600 22px Inter, -apple-system, sans-serif';
    ctx.fillStyle = '#e0e0f0';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    const maxTitleW = w - 140;
    let d = title;
    if (ctx.measureText(d).width > maxTitleW) {
      while (ctx.measureText(d + '…').width > maxTitleW && d.length > 0) d = d.slice(0, -1);
      d += '…';
    }
    ctx.fillText(d, 12, h / 2);

    ctx.font = '500 20px Inter, -apple-system, sans-serif';
    ctx.fillStyle = '#a29bfe';
    ctx.textAlign = 'right';
    ctx.fillText(timeStr || '0:00 / 0:00', w - 12, h / 2);

    this._textTexture.needsUpdate = true;
  }

  _renderProgress(fraction) {
    if (Math.abs(fraction - this._lastFraction) < 0.005) return;
    this._lastFraction = fraction;

    const ctx = this._progCtx;
    const w = this._progCanvas.width;
    const h = this._progCanvas.height;
    ctx.clearRect(0, 0, w, h);

    ctx.fillStyle = 'rgba(255,255,255,0.15)';
    ctx.beginPath();
    ctx.roundRect(0, 2, w, h - 4, 4);
    ctx.fill();

    const fillW = Math.max(4, fraction * w);
    ctx.fillStyle = '#6C5CE7';
    ctx.beginPath();
    ctx.roundRect(0, 2, fillW, h - 4, 4);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(fillW, h / 2, 6, 0, Math.PI * 2);
    ctx.fill();

    this._progTexture.needsUpdate = true;
  }

  // ─── Show / Hide ──────────────────────────────────────────────
  show() {
    this._isVisible = true;
    this._isOpen = true;
    this.group.visible = true;
    console.log('[VROverlay] Shown');
  }

  hide() {
    this._isVisible = false;
    this._isOpen = false;
    this.group.visible = false;
  }

  _close() {
    this._isOpen = false;
    this.group.visible = false;
    console.log('[VROverlay] Closed by user — pinch anywhere to reopen');
  }

  reopen() {
    if (!this._isVisible) return; // Only reopen during active XR
    this._isOpen = true;
    this.group.visible = true;
    console.log('[VROverlay] Reopened');
  }

  // ─── Update ───────────────────────────────────────────────────
  update() {
    if (!this._isVisible || !this._isOpen) return;

    if (this._isFollowing) {
      const cam = this.scene.getObjectByProperty('isCamera', true);
      if (cam) {
        const camPos = new THREE.Vector3();
        cam.getWorldPosition(camPos);
        const fwd = new THREE.Vector3();
        cam.getWorldDirection(fwd);
        this.group.position.set(
          camPos.x + fwd.x * 5,
          camPos.y - 0.2,
          camPos.z + fwd.z * 5
        );
        this.group.rotation.set(0, Math.atan2(fwd.x, fwd.z) + Math.PI, 0);
      }
    }

    const playing = this.mainScreen.isPlaying;
    this._playIcon.visible = !playing;
    this._pauseIcon.visible = playing;

    // Title from video element
    const vid = this.mainScreen.videoElement;
    let title = '';
    if (vid && vid.src) {
      title = decodeURIComponent(vid.src.split('/').pop() || '')
        .replace(/\.[^.]+$/, '')
        .replace(/[_-]/g, ' ');
    }
    // Show buffering state
    if (this.mainScreen.isBuffering && vid && vid.src) {
      const buf = vid.buffered;
      let bufferedSec = 0;
      const ct = vid.currentTime || 0;
      for (let i = 0; i < buf.length; i++) {
        if (buf.start(i) <= ct && buf.end(i) > ct) {
          bufferedSec = buf.end(i) - ct;
          break;
        }
      }
      title = `Buffering ${bufferedSec.toFixed(0)}s / 5s...`;
    } else if (!vid || !vid.src) {
      title = 'Waiting for media...';
    }

    const { currentTime, duration } = this.mainScreen;
    this._renderText(title, `${this._fmt(currentTime)} / ${this._fmt(duration)}`);
    this._renderProgress(duration > 0 ? currentTime / duration : 0);

    // Follow XR camera
    const xrCam = this.renderer.xr.getCamera();
    if (xrCam) {
      const dir = new THREE.Vector3(0, 0, -1).applyQuaternion(xrCam.quaternion);
      const pos = xrCam.position.clone().add(dir.multiplyScalar(2.0));
      pos.y -= 0.6;
      this.group.position.lerp(pos, 0.08);
      this.group.quaternion.slerp(xrCam.quaternion, 0.08);
    }
  }

  _fmt(s) {
    if (isNaN(s) || !s) return '0:00';
    return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  }

  // ─── Interaction ──────────────────────────────────────────────
  handleRaycast(raycaster) {
    if (!this._isVisible || !this._isOpen) return false;
    return raycaster.intersectObjects(this._allMeshes, false).length > 0;
  }

  /**
   * Handle select. Returns true if consumed.
   */
  handleSelect(raycaster) {
    // If overlay is closed, reopen on any pinch
    if (this._isVisible && !this._isOpen) {
      this.reopen();
      return true;
    }
    if (!this._isVisible || !this._isOpen) return false;

    const hits = raycaster.intersectObjects(this._clickables, false);
    if (hits.length > 0) {
      const action = hits[0].object.userData.action;
      console.log('[VROverlay] Action:', action);

      switch (action) {
        case 'togglePlay': {
          const vid = this.mainScreen.videoElement;
          if (vid) {
            if (vid.paused) {
              vid.play().then(() => {
                this.mainScreen.isPlaying = true;
                console.log('[VROverlay] Play');
              }).catch(e => console.error('[VROverlay] Play failed:', e));
            } else {
              vid.pause();
              this.mainScreen.isPlaying = false;
              console.log('[VROverlay] Pause');
            }
          } else {
            console.warn('[VROverlay] No video element');
          }
          this._flashBtn(this._btnPlay, 0xa29bfe, 0x6C5CE7);
          break;
        }
        case 'rewind':
          if (this.mainScreen.videoElement) {
            this.mainScreen.videoElement.currentTime = Math.max(0, this.mainScreen.videoElement.currentTime - 10);
          }
          this._flashBtn(this._btnRwd, 0x6C5CE7, 0x2a2a3c);
          break;
        case 'forward':
          if (this.mainScreen.videoElement) {
            const d = this.mainScreen.videoElement.duration || 0;
            this.mainScreen.videoElement.currentTime = Math.min(d, this.mainScreen.videoElement.currentTime + 10);
          }
          this._flashBtn(this._btnFwd, 0x6C5CE7, 0x2a2a3c);
          break;
        case 'seek': {
          const localPt = this._progMesh.worldToLocal(hits[0].point.clone());
          const frac = Math.max(0, Math.min(1, localPt.x / 1.6 + 0.5));
          if (this.mainScreen.videoElement?.duration) {
            this.mainScreen.videoElement.currentTime = frac * this.mainScreen.videoElement.duration;
          }
          break;
        }
        case 'close':
          this._close();
          break;
        case 'toggleMute':
          if (this.mainScreen?.videoElement) {
            this.mainScreen.videoElement.muted = !this.mainScreen.videoElement.muted;
            if (this._volIconMat) {
              this._volIconMat.map = this.mainScreen.videoElement.muted ? this._volMuteTex : this._volUnmuteTex;
              this._volIconMat.needsUpdate = true;
            }
            console.log('[VROverlay]', this.mainScreen.videoElement.muted ? '🔇 Muted' : '🔊 Unmuted');
          }
          this._flashBtn(this._btnVol, 0x6C5CE7, 0x444455);
          break;
        case 'toggleFollow':
          this._isFollowing = !this._isFollowing;
          if (this._pinIconMat) {
            this._pinIconMat.map = this._isFollowing ? this._followTex : this._pinTex;
            this._pinIconMat.needsUpdate = true;
          }
          console.log('[VROverlay]', this._isFollowing ? '👁 Following' : '📌 Pinned');
          this._flashBtn(this._btnPin, 0x6C5CE7, 0x444455);
          break;
      }
      return true;
    }

    // Hit panel background but not a button — still block ray
    const panelHits = raycaster.intersectObjects(this._allMeshes, false);
    return panelHits.length > 0;
  }

  _flashBtn(btn, flash, restore) {
    btn.material.color.setHex(flash);
    setTimeout(() => btn.material.color.setHex(restore), 250);
  }

  setLiquidGlass(enabled) {
    this._liquidGlass = !!enabled;
    if (this._panel) {
      if (enabled) {
        this._panel.material.color.setHex(0xdedede);
        this._panel.material.transparent = true;
        this._panel.material.opacity = 0.18;
      } else {
        this._panel.material.color.setHex(0x000000);
        this._panel.material.transparent = false;
        this._panel.material.opacity = 1;
      }
      this._panel.material.needsUpdate = true;
    }
    // Play button — frosted white vs purple
    if (this._btnPlay) {
      this._btnPlay.material.color.setHex(enabled ? 0xaaaaaa : 0x6C5CE7);
      this._btnPlay.material.transparent = enabled;
      this._btnPlay.material.opacity = enabled ? 0.5 : 1;
      this._btnPlay.material.needsUpdate = true;
    }
    // Rewind/Forward/Close buttons
    const rwdFwdColor = enabled ? 0x999999 : 0x2a2a3c;
    const btnColor = enabled ? 0x999999 : 0x444455;
    [this._btnRwd, this._btnFwd].forEach(btn => {
      if (btn) { btn.material.color.setHex(rwdFwdColor); btn.material.transparent = enabled; btn.material.opacity = enabled ? 0.35 : 1; btn.material.needsUpdate = true; }
    });
    if (this._btnClose) { this._btnClose.material.color.setHex(btnColor); this._btnClose.material.transparent = enabled; this._btnClose.material.opacity = enabled ? 0.35 : 1; this._btnClose.material.needsUpdate = true; }
    if (this._btnVol) { this._btnVol.material.color.setHex(btnColor); this._btnVol.material.transparent = enabled; this._btnVol.material.opacity = enabled ? 0.35 : 1; this._btnVol.material.needsUpdate = true; }
    if (this._btnPin) { this._btnPin.material.color.setHex(btnColor); this._btnPin.material.transparent = enabled; this._btnPin.material.opacity = enabled ? 0.35 : 1; this._btnPin.material.needsUpdate = true; }
    // Border — white translucent vs purple
    this.group.children.forEach(child => {
      if (child.renderOrder === 9998 && child.material) {
        child.material.color.setHex(enabled ? 0xffffff : 0x6C5CE7);
        child.material.opacity = enabled ? 0.12 : 0.2;
        child.material.needsUpdate = true;
      }
    });
  }

  get clickables() { return this._allMeshes; }
}
